import React, { useState, useEffect, useRef } from 'react';
import { API_URL } from '../../../config/api';

/**
 * FirmDocumentsWidget — compact version of DocumentsTab. Lists uploaded files
 * with download/delete and a compact upload button (hidden file input). Data
 * contract: GET/POST /firms/:id/documents ({id,filename,notes,uploadedBy,
 * uploadedAt}), GET .../:id/download (blob), DELETE /documents/:id.
 */
export default function FirmDocumentsWidget({ firmId }) {
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    const token = localStorage.getItem('authToken');

    useEffect(() => { fetchDocuments(); /* eslint-disable-next-line */ }, [firmId]);

    const fetchDocuments = async () => {
        try {
            const res = await fetch(`${API_URL}/firms/${firmId}/documents`, { headers: { Authorization: `Bearer ${token}` } });
            if (res.ok) setDocuments(await res.json());
        } catch (e) { console.error('Error fetching documents:', e); }
        finally { setLoading(false); }
    };

    const handleFilePicked = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) { alert('File size must be less than 10MB'); return; }
        try {
            setUploading(true);
            const formData = new FormData();
            formData.append('file', file);
            formData.append('uploadedBy', localStorage.getItem('userName') || 'Anonymous');
            formData.append('notes', '');
            const res = await fetch(`${API_URL}/firms/${firmId}/documents`, {
                method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData,
            });
            if (!res.ok) throw new Error('Failed to upload document');
            fetchDocuments();
        } catch (err) { alert(err.message); }
        finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
    };

    const handleDownload = async (doc) => {
        try {
            const res = await fetch(`${API_URL}/firms/${firmId}/documents/${doc.id}/download`, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) throw new Error('Failed to download document');
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = window.document.createElement('a');
            a.href = url; a.download = doc.filename;
            window.document.body.appendChild(a); a.click();
            window.URL.revokeObjectURL(url); window.document.body.removeChild(a);
        } catch (e) { alert(e.message); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this document?')) return;
        try {
            const res = await fetch(`${API_URL}/documents/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
            if (res.ok) fetchDocuments();
        } catch (e) { alert(e.message); }
    };

    const fileIconColor = (filename) => {
        const ext = filename.split('.').pop().toLowerCase();
        if (ext === 'pdf') return 'text-red-500';
        if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return 'text-blue-500';
        if (['doc', 'docx'].includes(ext)) return 'text-blue-600';
        if (['xls', 'xlsx'].includes(ext)) return 'text-green-600';
        return 'text-gray-400';
    };

    if (loading) return <p className="text-sm text-gray-400">Loading documents…</p>;

    return (
        <div className="space-y-3">
            <input ref={fileInputRef} id={`file-input-${firmId}`} type="file" onChange={handleFilePicked} className="hidden" />
            <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs font-medium text-emerald-700 border border-dashed border-emerald-300 rounded hover:bg-emerald-50 transition-colors disabled:opacity-50"
            >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                {uploading ? 'Uploading…' : 'Upload document'}
            </button>

            {documents.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-3">No documents yet</p>
            ) : (
                <div className="space-y-1.5">
                    {documents.map((doc) => (
                        <div key={doc.id} className="group flex items-center gap-2 px-2.5 py-2 bg-gray-50 rounded">
                            <svg className={`w-4 h-4 flex-shrink-0 ${fileIconColor(doc.filename)}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                            <div className="min-w-0 flex-1">
                                <div className="text-sm text-gray-800 truncate">{doc.filename}</div>
                                <div className="text-[10px] text-gray-400 truncate">{doc.uploadedBy} · {new Date(doc.uploadedAt).toLocaleDateString()}</div>
                            </div>
                            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                                <button onClick={() => handleDownload(doc)} className="p-1 text-gray-400 hover:text-emerald-600 rounded" title="Download">
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                                </button>
                                <button onClick={() => handleDelete(doc.id)} className="p-1 text-gray-400 hover:text-red-600 rounded" title="Delete">
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
